import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import {
  Box, Typography, Paper, Stack, TextField, Button, CircularProgress,
  Dialog, DialogContent, List, ListItemText, Divider,
  Chip, Avatar, alpha, useTheme, Autocomplete, Tooltip,
} from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import HistoryIcon from '@mui/icons-material/History';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import * as actions from '../../redux/actions';
import useMarketingLiveRefresh from './hooks/useMarketingLiveRefresh';
import { useMyEmployee } from './hooks/useMyEmployee';
import { showAlert } from '../../utils/functions/globalDialog';
import formatThaiDate from '../../utils/functions/formatThaiDate';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import SectionCard from './components/ui/SectionCard';
import ModalHeader from './components/ui/ModalHeader';
import { STATUS_LABELS, STATUS_ICONS, getStatusChipSx } from './constants/status';
import { TYPE_LABELS } from './constants/type';
import { formatValue } from './utils/format';

// สถานะที่ยังเปิดอยู่ (ยังไม่จบดีล) — เฉพาะ opportunity กลุ่มนี้เท่านั้นที่เช็คอินได้ ตรงกับ
// กลุ่มเดียวกับที่ใช้กรองในแท็บ "งานของผู้รับผิดชอบ"
const OPEN_STATUSES = 'WAITING,IN_PROGRESS,ON_HOLD';

// การ์ดโปรเจกต์ในลิสต์ — โชว์ข้อมูลโปรเจกต์ครบทุกอย่างที่พอมี (สถานะ, เลขที่โครงการ, ลูกค้า,
// ประเภทงาน, งบประมาณ, วันครบกำหนดยื่นราคา) กดที่การ์ดทั้งใบเพื่อเลือกโปรเจกต์นั้นขึ้นไปกรอกในฟอร์ม
// ด้านบน (แทนที่จะเปิด modal แยก — เอาไว้เป็นทางลัดคู่กับ dropdown เลือกโปรเจกต์) สไตล์เดียวกับการ์ด/
// แถวอื่นๆ ในโมดูล marketing (ไอคอน+ข้อความ, Chip พร้อมไอคอนสถานะ เหมือน OpportunitiesTable.js /
// TodayCheckinsView.js)
function ProjectCheckinCard({
  row, checked, checkinNames, selected, onClick,
}) {
  const theme = useTheme();
  const StatusIcon = STATUS_ICONS[row.status];
  const typeLabel = row.type === 'OTHER' ? (row.type_other || 'Other') : TYPE_LABELS[row.type];

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid',
        borderColor: selected ? 'primary.main' : (checked ? alpha(theme.palette.success.main, 0.35) : 'divider'),
        bgcolor: checked ? alpha(theme.palette.success.main, 0.05) : 'background.paper',
        boxShadow: selected ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.18)}` : 'none',
        cursor: 'pointer',
        transition: 'box-shadow .15s ease, transform .15s ease, border-color .15s ease',
        '&:hover': {
          borderColor: selected ? 'primary.main' : (checked ? 'success.main' : 'primary.main'),
          boxShadow: '0 12px 24px -12px rgba(15, 23, 42, 0.18)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <Avatar
          sx={{
            width: 46,
            height: 46,
            bgcolor: checked ? alpha(theme.palette.success.main, 0.15) : alpha(theme.palette.primary.main, 0.1),
            color: checked ? 'success.main' : 'primary.main',
            flexShrink: 0,
          }}
        >
          {checked ? <CheckCircleOutlinedIcon /> : <BusinessCenterOutlinedIcon />}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body1" fontWeight={700}>{row.project_name}</Typography>
            <Chip
              icon={StatusIcon ? <StatusIcon fontSize="small" /> : undefined}
              label={STATUS_LABELS[row.status] || row.status}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600, ...getStatusChipSx(row.status, 'outlined') }}
            />
          </Stack>

          <Stack direction="row" spacing={1.75} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mt: 0.75, rowGap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">{row.opportunity_number}</Typography>
            <Stack direction="row" spacing={0.4} alignItems="center">
              <PersonOutlineOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">{row.contact_name_snapshot || 'ไม่ระบุลูกค้า'}</Typography>
            </Stack>
            {typeLabel && (
              <Stack direction="row" spacing={0.4} alignItems="center">
                <LabelOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">{typeLabel}</Typography>
              </Stack>
            )}
            {(row.budget || row.budget === 0) && (
              <Stack direction="row" spacing={0.4} alignItems="center">
                <PaymentsOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">{`งบประมาณ ${formatValue(row.budget)}`}</Typography>
              </Stack>
            )}
            {row.quotation_due_date && (
              <Stack direction="row" spacing={0.4} alignItems="center">
                <EventOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {`เสนอราคา ${formatThaiDate(row.quotation_due_date)}`}
                </Typography>
              </Stack>
            )}
          </Stack>

          {checkinNames?.length > 0 && (
            <Tooltip
              title={checkinNames.map((p) => (p.planNote ? `${p.name}: ${p.planNote}` : p.name)).join('\n')}
              placement="top"
              componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}
            >
              <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mt: 0.75, cursor: 'default' }}>
                <PeopleAltOutlinedIcon sx={{ fontSize: 15, color: 'success.main', mt: '1px' }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical',
                  }}
                >
                  {`เช็คอินแล้ว: ${checkinNames.map((p) => (p.mine ? `${p.name} (ฉัน)` : p.name)).join(', ')}`}
                </Typography>
              </Stack>
            </Tooltip>
          )}
        </Box>

        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, alignSelf: { xs: 'flex-end', sm: 'center' } }}>
          <Chip
            label={checked ? 'เช็คอินแล้ว' : 'ยังไม่เช็คอิน'}
            size="small"
            color={checked ? 'success' : 'default'}
            variant={checked ? 'filled' : 'outlined'}
            sx={{ fontWeight: 700 }}
          />
          <ChevronRightIcon sx={{ color: 'text.disabled' }} />
        </Stack>
      </Stack>
    </Paper>
  );
}

// หน้า "เช็กอิน" — พนักงานฝ่ายการตลาด (system_role) เช็คอินโปรเจกต์ไหนก็ได้ที่ยังเปิดอยู่ ไม่จำกัด
// แค่โปรเจกต์ที่ตัวเองเป็นผู้รับผิดชอบ (owner) (เช็คสิทธิ์จริงที่ checkin.controller.js#checkinHandler
// ฝั่ง backend) เช็คอินได้หลายโปรเจกต์พร้อมกันในวันเดียว ไม่มี checkout — เช็คอินซ้ำวันเดียวกันแค่แก้
// plan_note (ดู checkin.repository.js#checkinToday) และ 1 โปรเจกต์เช็คอินได้หลายคนพร้อมกัน (unique
// key คือ opportunity+employee+วันที่ ไม่ใช่แค่ opportunity+วันที่)
//
// ดีไซน์ยึดตาม house style ของโมดูล marketing เป๊ะๆ ไม่ใช่ธีมแยกของหน้านี้เอง: หัวหน้าเพจ = ไอคอน
// กล่องไล่สี indigo + ข้อความไล่สีเดียวกัน (ตาม OpportunityList.js), การ์ดฟอร์ม = SectionCard (ตัวที่
// ContactInfoCard.js/OpportunityInfoCard.js ใช้), แถวโปรเจกต์ = ไอคอน+ข้อความ+Chip พร้อมไอคอนสถานะ
// (ตาม OpportunitiesTable.js/TodayCheckinsView.js), Dialog ประวัติใช้ ModalHeader ตัวเดียวกับทุก
// modal ในโมดูลนี้ — ฟอร์มเช็คอิน = dropdown เลือกโปรเจกต์ + ช่องกรอกแผนงาน + ปุ่มเช็คอิน อยู่บนสุด
// เสมอ (ไม่เปิด modal แยก) คลิกการ์ดในลิสต์ด้านล่างก็แค่เลือกโปรเจกต์นั้นขึ้นไปที่ dropdown เดียวกัน
export default function CheckinPage() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const myEmployee = useMyEmployee();
  const formRef = useRef(null);
  const [projects, setProjects] = useState(null); // null = กำลังโหลดครั้งแรก
  const [todayCheckins, setTodayCheckins] = useState([]); // เฉพาะของฉันเอง (ใช้เช็คปุ่ม/prefill note)
  const [overviewCheckins, setOverviewCheckins] = useState([]); // ของทุกคน (ใช้โชว์ชื่อคนเช็คอินในการ์ด)
  const [saving, setSaving] = useState(false);

  const [selectedId, setSelectedId] = useState(null); // opportunity id ที่เลือกอยู่ในฟอร์มด้านบน
  const [noteDraft, setNoteDraft] = useState('');

  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // ดึงพนักงานทั้งบริษัทครั้งเดียว (ไม่จำกัดฝ่าย) ไว้ resolve ชื่อคนเช็คอิน — ต่างจาก myEmployee ที่
  // resolve แค่ตัวเอง ใครก็เช็คอินได้ทุกโปรเจกต์แล้ว (ไม่ใช่แค่ผู้รับผิดชอบ) ชื่อที่โผล่ในการ์ดจึง
  // อาจเป็นพนักงานคนไหนก็ได้ ไม่ใช่แค่ฝ่ายการตลาด
  const employee = useSelector((state) => state.employee);
  useEffect(() => {
    dispatch(actions.employeeAll({ size: 9999, activeOnly: false }));
  }, [dispatch]);
  const employeeNameById = useMemo(() => {
    const map = {};
    (employee?.rows || []).forEach((e) => {
      map[e._id || e.id] = formatEmployeeThaiName(e);
    });
    return map;
  }, [employee?.rows]);

  const load = useCallback(async () => {
    if (!myEmployee?._id) return;
    const [listRes, checkinsRes, overviewRes] = await Promise.all([
      dispatch(actions.listOpportunities({
        status: OPEN_STATUSES,
        size: 999,
        sortBy: 'created_at',
        sortOrder: 'desc',
      })),
      dispatch(actions.myTodayCheckins()),
      dispatch(actions.todayCheckinsOverview()),
    ]);
    setProjects(listRes.data || []);
    setTodayCheckins(checkinsRes || []);
    setOverviewCheckins(overviewRes || []);
  }, [dispatch, myEmployee?._id]);

  useEffect(() => { load(); }, [load]);

  // เห็นการแก้/เช็คอินจากที่อื่นเอง (เงียบ ไม่โชว์ error)
  useMarketingLiveRefresh(async () => {
    if (!myEmployee?._id) return;
    const [listRes, checkinsRes, overviewRes] = await Promise.all([
      dispatch(actions.listOpportunities({
        status: OPEN_STATUSES,
        size: 999,
        sortBy: 'created_at',
        sortOrder: 'desc',
        silent: true,
      })),
      dispatch(actions.myTodayCheckins({ silent: true })),
      dispatch(actions.todayCheckinsOverview({ silent: true })),
    ]);
    const same = (prev, next) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next);
    setProjects((prev) => same(prev, listRes.data || []));
    setTodayCheckins((prev) => same(prev, checkinsRes || []));
    setOverviewCheckins((prev) => same(prev, overviewRes || []));
  }, { enabled: Boolean(myEmployee?._id) });

  // เช็คอินวันนี้ของแต่ละ opportunity (เฉพาะของฉัน) — key ด้วย opportunity_id ให้หาเร็ว
  const checkinByOpportunity = useMemo(() => {
    const map = {};
    todayCheckins.forEach((c) => { map[c.opportunity_id] = c; });
    return map;
  }, [todayCheckins]);

  // รายชื่อคนที่เช็คอินโปรเจกต์นี้ไปแล้ววันนี้ (ทุกคน) — key ด้วย opportunity_id, ชื่อของตัวเองถูก
  // ตั้ง mine:true และดันขึ้นก่อนเสมอ ให้สแกนไวว่า "ฉันเช็คอินรึยัง"
  const checkinNamesByOpportunity = useMemo(() => {
    const map = {};
    overviewCheckins.forEach((c) => {
      const name = employeeNameById[c.employee_id] || '(ไม่ทราบชื่อ)';
      const mine = c.employee_id === myEmployee?._id;
      if (!map[c.opportunity_id]) map[c.opportunity_id] = [];
      map[c.opportunity_id].push({
        employeeId: c.employee_id, name, planNote: c.plan_note || '', mine,
      });
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => (a.mine === b.mine ? 0 : a.mine ? -1 : 1)));
    return map;
  }, [overviewCheckins, employeeNameById, myEmployee?._id]);

  const selectedProject = useMemo(
    () => (projects || []).find((p) => p.id === selectedId) || null,
    [projects, selectedId],
  );
  const selectedChecked = selectedId ? !!checkinByOpportunity[selectedId] : false;

  const selectProject = (row) => {
    setSelectedId(row ? row.id : null);
    setNoteDraft(row ? (checkinByOpportunity[row.id]?.plan_note || '') : '');
    if (row) {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleConfirmCheckin = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await dispatch(actions.checkinOpportunity({ id: selectedId, planNote: noteDraft }));
      await load();
    } catch (err) {
      showAlert(err.message || 'เช็คอินไม่สำเร็จ', { status: err?.response?.status });
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const data = await dispatch(actions.checkinHistory({ employeeId: myEmployee._id }));
      setHistory(data || []);
    } catch (err) {
      showAlert(err.message || 'โหลดประวัติไม่สำเร็จ', { status: err?.response?.status });
    } finally {
      setHistoryLoading(false);
    }
  };

  // จัดกลุ่มประวัติตามวันที่ เรียงล่าสุดก่อน — ใช้แสดงใน dialog ประวัติ
  const historyByDate = useMemo(() => {
    const map = {};
    history.forEach((c) => {
      const key = dayjs(c.checkin_date).format('YYYY-MM-DD');
      if (!map[key]) map[key] = [];
      map[key].push(c);
    });
    return Object.entries(map).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [history]);

  if (!myEmployee) {
    return (
      <Box sx={{ textAlign: 'center', py: 10, color: 'text.secondary' }}>
        <EventAvailableOutlinedIcon sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
        <Typography variant="body1">ไม่พบข้อมูลพนักงานที่ผูกกับบัญชีนี้ — เช็คอินไม่ได้</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 3 }} flexWrap="wrap" rowGap={2}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, rgb(var(--color-primary-700)) 0%, rgb(var(--color-primary-450)) 100%)',
              color: '#fff',
            }}
          >
            <EventAvailableOutlinedIcon />
          </Box>
          <Box>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                backgroundImage: 'linear-gradient(135deg, rgb(var(--color-primary-700)), rgb(var(--color-primary-450)))',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
              }}
            >
              เช็กอิน
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {`วันนี้ ${formatThaiDate(dayjs())} · เช็คอินโปรเจกต์ที่กำลังทำอยู่ พร้อมแผนงานคร่าวๆ`}
            </Typography>
          </Box>
        </Stack>
        <Button variant="outlined" startIcon={<HistoryIcon />} onClick={openHistory} sx={{ borderRadius: 2 }}>
          ประวัติของฉัน
        </Button>
      </Stack>

      <Box ref={formRef} sx={{ mb: 3 }}>
        <SectionCard icon={<EventAvailableOutlinedIcon />} title="เช็คอินโปรเจกต์" accentColor="primary.main">
          <Stack spacing={2}>
            <Autocomplete
              options={projects || []}
              loading={projects === null}
              value={selectedProject}
              onChange={(e, v) => selectProject(v)}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              getOptionLabel={(o) => o.project_name || ''}
              renderOption={(props, option) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <li {...props} key={option.id}>
                  <Stack sx={{ width: '100%' }} spacing={0.2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                      <Typography variant="body2" fontWeight={600}>{option.project_name}</Typography>
                      {checkinByOpportunity[option.id] && (
                        <Chip label="เช็คอินแล้ว" size="small" color="success" sx={{ fontWeight: 700, height: 20 }} />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {option.opportunity_number}
                      {option.contact_name_snapshot ? ` · ${option.contact_name_snapshot}` : ''}
                    </Typography>
                  </Stack>
                </li>
              )}
              renderInput={(params) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <TextField {...params} label="เลือกโปรเจกต์" placeholder="ค้นหาชื่อโปรเจกต์ / เลขที่โครงการ" />
              )}
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="แผนงานวันนี้"
              placeholder="เช่น ติดตามใบเสนอราคา, นัดพบลูกค้า"
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              disabled={!selectedId}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleConfirmCheckin}
                disabled={!selectedId || saving}
                startIcon={saving ? undefined : (selectedChecked ? <NoteAltOutlinedIcon /> : <CheckCircleOutlinedIcon />)}
                sx={{
                  borderRadius: 2,
                  minWidth: 140,
                  boxShadow: `0 8px 20px -6px ${alpha(theme.palette.primary.main, 0.5)}`,
                }}
              >
                {saving ? <CircularProgress size={18} color="inherit" /> : (selectedChecked ? 'บันทึกแผนงาน' : 'เช็คอิน')}
              </Button>
            </Box>
          </Stack>
        </SectionCard>
      </Box>

      {projects === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      ) : projects.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10, color: 'text.secondary' }}>
          <EventAvailableOutlinedIcon sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
          <Typography variant="body2">ไม่มีโปรเจกต์ที่ต้องเช็คอินตอนนี้</Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {projects.map((row) => (
            <ProjectCheckinCard
              key={row.id}
              row={row}
              checked={!!checkinByOpportunity[row.id]}
              checkinNames={checkinNamesByOpportunity[row.id]}
              selected={row.id === selectedId}
              onClick={() => selectProject(row)}
            />
          ))}
        </Stack>
      )}

      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, height: '80vh' } }}
      >
        <ModalHeader
          icon={<HistoryIcon fontSize="small" />}
          title="ประวัติเช็คอินของฉัน"
          onClose={() => setHistoryOpen(false)}
        />
        <DialogContent sx={{ p: 0 }}>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : historyByDate.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
              <Typography variant="body2">ยังไม่มีประวัติเช็คอิน</Typography>
            </Box>
          ) : (
            historyByDate.map(([date, items]) => (
              <Box key={date}>
                <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
                  <Typography variant="caption" fontWeight={700}>
                    {formatThaiDate(date)}
                  </Typography>
                </Box>
                <List disablePadding>
                  {items.map((c) => (
                    <React.Fragment key={c.id}>
                      <ListItemText
                        sx={{ px: 2, py: 1 }}
                        primary={c.marketing_opportunities?.project_name || '-'}
                        secondary={c.plan_note || 'ไม่มีบันทึกแผนงาน'}
                      />
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            ))
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
